import { api } from "@/api/base";
import idb from "@/idb";
import config from "@/config";
import { log, sentry } from "@/log";
import { setLanguageAsync, loadLanguageAsync } from "@/i18n";
import { useEditorStore } from "@/pinia/editor";
import { useMainStore } from "@/pinia/main";
import { useAuthStore } from "@/pinia/auth";

export async function initGuard(to, from, next) {
  const mainStore = useMainStore();
  if (!mainStore.initialized) {
    await mainStore.initApp();
  }
  return next();
}

export function languageGuard(to, from, next) {
  const mainStore = useMainStore();
  const editorLocale = mainStore.editorLocale;
  // For routes that can be in any language, do nothing
  if (to.matched.some((record) => record.meta.isPublic)) {
    return next();
  }
  // For routes that are previews, only ensure that editor locale is loaded
  if (to.matched.some((record) => record.meta.isPreview)) {
    if (editorLocale) loadLanguageAsync(editorLocale);
    return next();
  }
  // At this point we are on an editor page and should adhere to editor locale
  // if present
  if (editorLocale && editorLocale !== mainStore.locale) {
    setLanguageAsync(editorLocale);
  }
  return next();
}

export function authGuard(to, from, next) {
  const authStore = useAuthStore();
  if (to.matched.some((record) => record.meta.requiresAuth)) {
    if (!authStore.isAuthenticated) {
      // Avoid redirecting to login (should never happen)
      console.warn(
        `Auth guard from ${from.fullPath} to ${to.fullPath}: not authenticated, redirecting to login`
      );
      const query = to.name !== "Login" ? { redirect: to.fullPath } : undefined;
      return next({ path: "/auth/login", query });
    }
  }
  if (to.matched.some((record) => record.meta.requiresUnauth)) {
    if (authStore.isAuthenticated) {
      authStore.logout(); // The choice to not wait here is NOT deliberate, just historical
    }
  }
  return next();
}

export function groupGuard(to, from, next) {
  if (to.matched.some((record) => record.meta.requiresGroup)) {
    if (!config.accessConfig.allowGroupCreation && !useMainStore().hasGroups) {
      return next({ path: "/" });
    }
  }
  return next();
}

export async function idbGuard(to, from, next) {
  if (to.matched.some((record) => record.meta.isPublic)) return next();
  await idb.init().catch(log);
  return next();
}

export function disabledGuard(to, from, next) {
  if (useMainStore().user?.disabled && to.name !== "UserDisabled") {
    return next({ name: "UserDisabled" });
  }
  return next();
}

export function resetEditorStateGuard(to, from, next) {
  useEditorStore().resetEditorState();
  return next();
}

export async function forceActiveGroupGuard(to, from, next) {
  // no meta -> pass
  if (!to.meta) {
    return next();
  }
  // no force -> pass
  const forceGroupIdBy = to.meta.forceGroupIdBy;
  if (!forceGroupIdBy) {
    return next();
  }
  if (!useAuthStore().isAuthenticated) {
    return next();
  }
  // target id and current active id
  let desiredGroupId;
  const currentGroupId = useMainStore().activeGroupId;
  // Do we want to force active group by questionnaire
  if (forceGroupIdBy === "questionnaireId") {
    const desiredQuestionnaireId = to.params["questionnaireId"];
    if (!desiredQuestionnaireId) {
      // Someone is using the forceGroupIdBy meta attribute without the
      // correct route parameter being present.
      sentry("routeGuard: no questionnaire id in path", {
        class: "debug",
      });
      return next();
    }

    let questionnaireResponse;
    try {
      questionnaireResponse = await api.post("/questionnaire/get", {
        questionnaireId: desiredQuestionnaireId,
      });
    } catch (error) {
      // This is likely due to insufficient permissions -> bounce
      log("routeGuard: COULD NOT GET QUESTIONNAIRE");
      return next({ path: "/" });
    }
    desiredGroupId = questionnaireResponse.data.response.ownerGroupId;
  }
  // or do we want to force active group by post
  else if (forceGroupIdBy === "postId") {
    const postId = to.params["postId"];
    if (!postId) {
      // Someone is using the forceGroupIdBy meta attribute without the
      // correct route parameter being present.
      sentry("routeGuard: no post id in path", {
        class: "debug",
      });
      return next();
    }
    // We can not avoid the server round trip b/c post editor does not use store
    // so no way to access currently loaded post. So, fetch and deduce groupId
    let postResponse;
    try {
      postResponse = await api.post("/post/get", { postId });
    } catch (error) {
      // This is likely due to insufficient permissions -> bounce
      log("routeGuard: COULD NOT GET POST");
      return next({ path: "/" });
    }
    desiredGroupId = postResponse.data.response.ownerGroupId;
  }
  // or do we want to force active group by post
  else if (forceGroupIdBy === "projectId") {
    const projectId = to.params["projectId"];
    if (!projectId) {
      // Someone is using the forceGroupIdBy meta attribute without the
      // correct route parameter being present.
      sentry("routeGuard: no group id in path", {
        class: "debug",
      });
      return next();
    }
    // We can not avoid the server round trip b/c post editor does not use store
    // so no way to access currently loaded post. So, fetch and deduce groupId
    let projectResponse;
    try {
      projectResponse = await api.post("/participation/project/get ", { projectId });
    } catch (error) {
      // This is likely due to insufficient permissions -> bounce
      log("routeGuard: COULD NOT GET POST");
      return next({ path: "/" });
    }
    desiredGroupId = projectResponse.data.response.ownerGroupId;
  }
  // someone is using nonstandard value for forceGroupId.
  else {
    sentry(`routeGuard: unknown ID parameter ${forceGroupIdBy}`, {
      class: "debug",
    });
    return next();
  }

  // So now we (should) have the desired groupId
  if (!desiredGroupId) {
    // Technically no traversal of the above code should lead to this state
    sentry("routeGuard: could not deduce desired group ID", {
      class: "shit-fan",
    });
    return next();
  }

  // Are we already in the correct group?
  if (currentGroupId === desiredGroupId) {
    log("routeGuard: HAD TO DIG AROUND BUT NOW EVERYTHING IS OK");
    return next();
  }

  // Sigh, so we need to actually switch groups
  let groupResponse;
  try {
    groupResponse = await api.post("/user/group/view", { groupId: desiredGroupId });
  } catch (error) {
    sentry("routeGuard: switching group for admin user failed", {
      class: "shit-fan",
    });
    return next(false);
  }
  useMainStore().setActiveGroup(groupResponse.data.response);
  await useMainStore().getLicenses(groupResponse.data.response.groupId);
  await useMainStore().getAccessLevels(groupResponse.data.response.groupId);
  log("routeGuard: HAD TO ACTUALLY SWITCH GROUPS");
  return next();
}

export default {
  idbGuard,
  initGuard,
  authGuard,
  groupGuard,
  disabledGuard,
  languageGuard,
  forceActiveGroupGuard,
  resetEditorStateGuard,
};
